import { type PlayableRecording, useGlobalPlayer } from "@bandwith/recordings";
import { formatDuration } from "@bandwith/time";
import { Button } from "@bandwith/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, EyeOff, Play } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { FeedbackList } from "../components/FeedbackList";
import { SetlistEditor } from "../components/SetlistEditor";
import { setlistEntryHref } from "../components/SetlistNavigation";
import {
  type MediaItem,
  SortableMediaList,
} from "../components/SortableMediaList";
import { useIsAdmin, useSession } from "../lib/auth";
import { setlistFeedbackQuery, setlistQuery, songsQuery } from "../lib/queries";
import { useEditActionParam } from "../lib/useEditActionParam";
import { useFeedbackCache } from "../lib/useFeedbackCache";
import { useSignInRedirect } from "../lib/useSignInRedirect";
import { setlistDetailRoute } from "../router";

export function SetlistDetail() {
  const { id } = setlistDetailRoute.useParams();
  const { action: initialAction } = setlistDetailRoute.useSearch();
  const { session } = useSession();
  const signInRedirect = useSignInRedirect();
  const play = useGlobalPlayer((s) => s.play);
  const queryClient = useQueryClient();
  const isSignedIn = !!session?.user;
  const isAdmin = useIsAdmin();
  const [isEditing, setIsEditing] = useState(false);

  const { data: setlist, isLoading, error } = useQuery(setlistQuery(id));

  // Only the editor's song picker needs every song
  const { data: allSongs = [], isLoading: allSongsLoading } = useQuery({
    ...songsQuery(),
    enabled: isSignedIn && isEditing,
  });

  const { data: feedback = [] } = useQuery(setlistFeedbackQuery(id));

  // Every take across the setlist's songs, each carrying its song's metadata,
  // so [filename @ 1:30] chips can resolve and queue any of them
  const playableRecordings: PlayableRecording[] = useMemo(() => {
    if (!setlist?.songs) return [];
    const byId = new Map<string, PlayableRecording>();
    for (const item of setlist.songs) {
      if (!item.song) continue;
      const songMeta = {
        songId: item.song.id,
        songTitle: item.song.title,
        authorName: item.song.author?.name ?? null,
      };
      for (const r of item.song.recordings ?? []) {
        byId.set(r.id, {
          id: r.id,
          filename: r.filename,
          signedAudioUrl: r.signedAudioUrl,
          ...songMeta,
        });
      }
      // The resolved take may be unlisted and thus missing from the list above
      if (item.primaryRecording) {
        byId.set(item.primaryRecording.id, {
          id: item.primaryRecording.id,
          filename: item.primaryRecording.filename,
          signedAudioUrl: item.primaryRecording.signedAudioUrl,
          ...songMeta,
        });
      }
    }
    return [...byId.values()];
  }, [setlist?.songs]);

  const feedbackCache = useFeedbackCache(setlistFeedbackQuery(id).queryKey);

  const mediaItems: MediaItem[] = useMemo(() => {
    if (!setlist?.songs) return [];

    return setlist.songs.map((item) => {
      const { song } = item;
      // A song not kept in Bandwith: just its title, on a page of its own
      if (!song) {
        return {
          id: item.id,
          title: item.title ?? "",
          linkHref: setlistEntryHref(id, item),
        };
      }
      return {
        id: item.id,
        title: song.title,
        // Surface the take's filename when this setlist pins a non-default one
        subtitle:
          item.recordingId && item.primaryRecording
            ? item.primaryRecording.filename
            : undefined,
        linkHref: `/songs/${song.id}?from=setlist-${id}`,
        audio: item.primaryRecording
          ? {
              recordingId: item.primaryRecording.id,
              filename: item.primaryRecording.filename,
              signedUrl: item.primaryRecording.signedAudioUrl,
              songId: song.id,
              songTitle: song.title,
              authorName: song.author?.name ?? null,
              durationSeconds: item.primaryRecording.durationSeconds ?? 0,
            }
          : null,
      };
    });
  }, [setlist?.songs, id]);

  const handlePlayAll = () => {
    const tracks = mediaItems
      .filter((item) => item.audio != null)
      .map((item) => ({
        id: item.audio!.recordingId,
        filename: item.audio!.filename,
        signedAudioUrl: item.audio!.signedUrl,
        songId: item.audio!.songId,
        songTitle: item.audio!.songTitle,
        authorName: item.audio!.authorName,
      }));
    if (tracks.length > 0) {
      play(tracks, 0, false);
    }
  };

  const handleSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: setlistQuery(id).queryKey });
    setIsEditing(false);
  }, [queryClient, id]);

  const handleCancelled = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleEditClick = () => {
    if (!isSignedIn) {
      signInRedirect(`/setlists/${id}?action=edit`);
      return;
    }
    setIsEditing(true);
  };

  // Auto-enter edit mode when ?action=edit is in URL (after OAuth redirect)
  useEditActionParam(
    initialAction,
    !!setlist && !("error" in setlist) && isSignedIn && !isEditing,
    () => setIsEditing(true),
  );

  if (isLoading) {
    return <div className="text-text-muted">Loading setlist...</div>;
  }

  if (error || !setlist || "error" in setlist) {
    return (
      <div>
        <p className="text-red-500">Setlist not found</p>
        <Link to="/setlists" className="text-amber hover:underline">
          Back to setlists
        </Link>
      </div>
    );
  }

  if (isEditing) {
    if (allSongsLoading) {
      return <div className="text-text-muted">Loading songs...</div>;
    }

    return (
      <SetlistEditor
        mode="edit"
        setlist={setlist}
        allSongs={allSongs}
        onSaved={handleSaved}
        onCancelled={handleCancelled}
      />
    );
  }

  const songCount = setlist.songs.length;

  return (
    <>
      {/* Back link with edit button */}
      <div className="mb-8 flex items-center justify-between">
        <Link
          to="/setlists"
          className="inline-flex items-center gap-1 text-sm text-text-muted transition-colors hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All Setlists
        </Link>
        <Button variant="ghost" onClick={handleEditClick}>
          Edit
        </Button>
      </div>

      {/* Header */}
      <header className="mb-8">
        <h1 className="font-display text-3xl text-text-primary">
          {setlist.name}
        </h1>
        {setlist.unlisted && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded border border-border-subtle px-1.5 py-0.5 font-mono text-xs uppercase tracking-widest text-text-muted">
            <EyeOff className="h-3 w-3" aria-hidden="true" />
            Unlisted
          </p>
        )}
        {setlist.createdBy && (
          <p className="mt-2 text-text-muted">
            Created by {setlist.createdBy.name}
          </p>
        )}
      </header>

      {/* Notes */}
      {setlist.notes && (
        <div className="mb-8 whitespace-pre-wrap text-text-secondary">
          {setlist.notes}
        </div>
      )}

      {/* Song list */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
            Songs ({songCount})
            {setlist.totalDurationSeconds > 0 &&
              ` · ${formatDuration(setlist.totalDurationSeconds)}`}
          </h2>
          {songCount > 0 && mediaItems.some((item) => item.audio) && (
            <button
              type="button"
              onClick={handlePlayAll}
              className="inline-flex items-center gap-2 rounded-md bg-amber px-3 py-1.5 text-sm font-medium text-on-accent transition-colors hover:bg-amber-warm"
            >
              <Play className="h-4 w-4" aria-hidden="true" />
              Play All
            </button>
          )}
        </div>
        {songCount === 0 ? (
          <p className="text-text-muted">No songs in this setlist yet.</p>
        ) : (
          <SortableMediaList
            items={mediaItems}
            isEditing={false}
            testIdPrefix="setlist-song"
          />
        )}
      </section>

      {/* Feedback section */}
      <section className="mt-10">
        <h2 className="mb-4 font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
          Feedback ({feedback.length})
        </h2>
        <FeedbackList
          feedback={feedback}
          target={{
            setlistId: id,
            recordingIds: playableRecordings.map((r) => r.id),
          }}
          recordings={playableRecordings}
          isAdmin={isAdmin}
          onFeedbackCreated={feedbackCache.handleFeedbackCreated}
          onFeedbackUpdated={feedbackCache.handleFeedbackUpdated}
          onFeedbackDeleted={feedbackCache.handleFeedbackDeleted}
        />
      </section>
    </>
  );
}
